import { formatDateTime } from '../../lib/format'

/**
 * Recent activity across the whole Space — the Space audit trail (task
 * created / status-changed / deleted, Space created·updated·deleted, member
 * added·removed), newest first. Each row shows the actor's name + a human
 * phrase built from the event's `action` + `metadata` + the date & time
 * (canonical `formatDateTime`, per-tenant timezone).
 *
 * Sourced from `overview.recentActivity` (AuditLog stamped with this Space).
 * No click target — audit rows don't map to a single canonical view.
 */

/** Turn one audit event into a readable phrase — kept in sync with the same
 *  wording the backend records. */
function summarise(action, metadata) {
  const name = metadata?.name ? `"${metadata.name}"` : ''
  // Space-level events carry the Space name in `metadata.name`; fall back to
  // the generic reference for any legacy event recorded without it.
  const spaceRef = name ? `the Space ${name}` : 'this Space'
  switch (action) {
    case 'tasks.created':
      return `created task ${name}`.trim()
    case 'tasks.deleted':
      return `deleted task ${name}`.trim()
    case 'tasks.status_changed':
      return `moved task ${name} from ${metadata?.from ?? '—'} to ${
        metadata?.to ?? '—'
      }`.trim()
    case 'list.created':
      return `created the List ${name}`.trim()
    case 'space.created':
      return `created ${spaceRef}`
    case 'space.updated':
      return metadata?.moved ? `moved ${spaceRef}` : `updated ${spaceRef}`
    case 'space.deleted':
      return `deleted ${spaceRef}`
    case 'space.member_added': {
      const n = metadata?.count ?? 0
      return `added ${n} member${n === 1 ? '' : 's'} to this Space`
    }
    case 'space.member_removed':
      return metadata?.self
        ? 'left this Space'
        : 'removed a member from this Space'
    default:
      return String(action ?? 'made a change').replace(/[._]/g, ' ')
  }
}

export function SpaceOverviewActivity({ overview }) {
  const events = overview?.recentActivity ?? []
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)] mb-3">
        Recent activity
      </p>
      {events.length === 0 ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          No recent activity in this Space yet.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--color-rule)]">
          {events.map((e) => (
            <li
              key={e.id}
              className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0 text-sm"
            >
              <span className="min-w-0 flex-1 break-words leading-relaxed">
                <span className="font-medium text-[color:var(--color-ink-strong)]">
                  {e.actorName ?? 'Someone'}
                </span>{' '}
                <span className="text-[color:var(--color-ink-muted)]">
                  {summarise(e.action, e.metadata)}
                </span>
              </span>
              <span className="mt-0.5 shrink-0 whitespace-nowrap text-xs tabular-nums text-[color:var(--color-ink-subtle)]">
                {formatDateTime(e.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

